import dayjs, { Dayjs } from 'dayjs'
import { getShipmentDisplayCalculated } from '../../../../helpers/entities/shipment/get-shipment-display-calculated'
import PrintQueueEntry from '../../../../types/api/print-queue-entry'

export const PRINT_PLAN_RESERVE_MINUTES = 24 * 60
export const PRINT_PLAN_DAYS_COUNT = 6

export const PRINT_PLAN_MACHINE_CARDS = [
    { key: 'Durst', title: 'Durst', brandNames: ['Durst'], showStats: true },
    { key: 'Bobst', title: 'Bobst', brandNames: ['Bobst'], showStats: true },
    { key: 'MPS', title: 'MPS', brandNames: ['MPS'], showStats: true },
    { key: 'Omet', title: 'Omet', brandNames: ['Omet'], showStats: true },
    { key: 'WG', title: 'WG', brandNames: ['WG'], showStats: true },
    {
        key: 'GF / RG / DCMQ',
        title: 'GF / RG / DCMQ',
        brandNames: ['GF', 'RG', 'DCMQ', 'DSMQ'],
        showStats: false,
    },
    {
        key: 'DMS / Термо',
        title: 'DMS / Термо',
        brandNames: ['DMS', 'Термо'],
        showStats: false,
    },
]

export interface PrintPlanMachineCard {
    key: string
    title: string
    brandNames: string[]
    showStats: boolean
}

export interface PrintPlanDateGroup {
    key: string
    deliveryDate: string
    dayOfWeek: string
    dateLabel: string
    printStartAt: string
    readyAt: string
    entries: PrintQueueEntry[]
    jobsCount: number
    totalArea: number
    totalMinutes: number
}

const DAY_OF_WEEK_LABELS = ['ВС', 'ПН', 'ВТ', 'СР', 'ЧТ', 'ПТ', 'СБ']

export function getPrintQueueEntryBrandName(entry: PrintQueueEntry) {
    return entry.machine?.machine_brand?.name || ''
}

export function getPrintQueueEntryDeliveryDate(entry: PrintQueueEntry) {
    const deliveryDate = entry.print_job?.shipment?.clientDeliveryDate
    return deliveryDate ? dayjs(deliveryDate).format('YYYY-MM-DD') : ''
}

export function getPrintQueueEntryMinutes(entry: PrintQueueEntry) {
    return getShipmentDisplayCalculated(entry.print_job?.shipment)?.commonTime || 0
}

export function getPrintQueueEntryAreaM2(entry: PrintQueueEntry) {
    const shipment = entry.print_job?.shipment
    const calculated = getShipmentDisplayCalculated(shipment)
    const copies =
        shipment?.shipment_positions?.reduce((acc, position) => acc + (position.copies || 0), 0) ||
        0
    const labelArea = calculated?.labelArea || 0

    if (copies && labelArea) {
        return (copies * labelArea) / 1000000
    }

    return calculated?.materialLength || 0
}

export function filterPrintQueueEntriesByBrands(entries: PrintQueueEntry[], brandNames: string[]) {
    return entries.filter(entry => brandNames.includes(getPrintQueueEntryBrandName(entry)))
}

/**
 * Ищет все отгрузки с датой поставки на выбранную дату
 */
export function getEntriesByDeliveryDate(entries: PrintQueueEntry[], deliveryDate: string) {
    return entries.filter(entry => getPrintQueueEntryDeliveryDate(entry) === deliveryDate)
}

/**
 * Суммирует все минуты печати переданных отгрузок
 */
export function getTotalMinutes(entries: PrintQueueEntry[]) {
    return entries.reduce((acc, entry) => acc + getPrintQueueEntryMinutes(entry), 0)
}

export function getTotalArea(entries: PrintQueueEntry[]) {
    return entries.reduce((acc, entry) => acc + getPrintQueueEntryAreaM2(entry), 0)
}

export function buildPrintPlanDateGroups(
    entries: PrintQueueEntry[],
    startDate: Dayjs = dayjs().add(1, 'day').startOf('day'),
    daysCount = PRINT_PLAN_DAYS_COUNT
): PrintPlanDateGroup[] {
    // Суточный запас один раз в начале, далее — каскад по дням
    let endDateTimeOfLastDate = startDate.add(PRINT_PLAN_RESERVE_MINUTES, 'minute')

    return Array.from({ length: daysCount }, (_, index) => {
        const currentDate = startDate.add(index, 'day').startOf('day')
        const deliveryDate = currentDate.format('YYYY-MM-DD')

        // Все отгрузки с датой поставки на текущую дату
        const dayEntries = getEntriesByDeliveryDate(entries, deliveryDate)
        // Минуты печати переданных отгрузок
        const totalMinutes = getTotalMinutes(dayEntries)
        // Площадь переданных отгрузок
        const totalArea = getTotalArea(dayEntries)
        // Время начала печати тиражей на текущую дату. Дата и время когда начнется печать тиражей
        const printStartAt = endDateTimeOfLastDate
        // Считаем во сколько будет готово
        const readyAt = printStartAt.add(totalMinutes, 'minute')

        // Устанавливаем дату готовности всех тиражей по текущей дате и времени окончания печати
        endDateTimeOfLastDate = readyAt

        const group: PrintPlanDateGroup = {
            key: deliveryDate,
            deliveryDate,
            dayOfWeek: DAY_OF_WEEK_LABELS[currentDate.day()],
            dateLabel: currentDate.format('DD.MM'),
            printStartAt: printStartAt.toISOString(),
            readyAt: readyAt.toISOString(),
            entries: dayEntries,
            jobsCount: dayEntries.length,
            totalArea,
            totalMinutes,
        }

        return group
    })
}
